'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ControlledInput, Typography } from 'components';
import PasswordValidator from 'components/PasswordValidator';
import { PRIVACY_POLICY_LINK, TERMS_CONDITIONS_LINK } from 'constants/links';
import { Maybe } from 'gql/graphql';
import { handlerEnter } from 'handlers/handlerEnter';
import { onErrorHelper } from 'handlers/handlerMutations';
import { toaster } from 'helpers/toaster';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useMemberInvite } from 'providers/AuthProviders/useMemberInvite';
import { useMemberPassword } from 'providers/AuthProviders/useMemberPassword';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { notifyException } from 'services/appsignal';
import { KeysEnum } from 'types/KeysEnum';
import { TGenericError } from 'types/TGenericError';
import { getQueryParam } from 'utils/paramsUtil';
import * as yup from 'yup';
import YupPassword from 'yup-password';

YupPassword(yup);

export default function AcceptInvitation() {
  const router = useRouter();
  const t = useTranslations();

  const token = getQueryParam(KeysEnum.Token) as string;

  const { data: memberInvite, isFetched } = useMemberInvite(
    {
      token
    },
    { enabled: !!token }
  );

  const schema = yup.object().shape({
    password: yup.string().password().required(t('messages.fields.password_required')),
    passwordConfirmation: yup
      .string()
      .required(t('messages.fields.confirm_password_required'))
      .test(
        'passwordConfirmation',
        t('messages.fields.password_must_match'),
        function (value: string | undefined) {
          return this.parent.password === value;
        }
      ),
    termsAccepted: yup
      .boolean()
      .is([true], t('messages.fields.accept_terms'))
      .required(t('messages.fields.accept_terms_required'))
  });

  type MemberInfo = {
    email?: string;
    companyName?: Maybe<string>;
    password: string;
    passwordConfirmation: string;
    termsAccepted: boolean;
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
    reset
  } = useForm<MemberInfo>({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    if (isFetched) {
      reset({
        email: memberInvite?.email,
        companyName: memberInvite?.companyName
      });
    }
  }, [isFetched, memberInvite, reset]);

  const password = watch('password');

  const { mutateAsync: memberPassword } = useMemberPassword({
    onSuccess: () => toaster.success(t('messages.success.accept_invite')),
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const onFetch = useCallback(async () => {
    try {
      const data = getValues();
      await memberPassword({
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
        token
      });

      return router.push('/auth/sign-in');
    } catch (error) {
      notifyException(error);
    }
  }, [getValues, memberPassword, router, token]);

  const onSubmit = handleSubmit(onFetch);

  return (
    <>
      <div className="flex flex-col">
        <Typography variant="medium7xl" className={'text-tertiary-100'}>
          {t('auth.accept_invite.title')}
        </Typography>
        <span>
          <Typography variant="regular4" className={'text-gray-700'}>
            {t('auth.sign_up.select_message')}
            <Link href="/auth/sign-in" className="ml-1 cursor-pointer text-sm text-secondary-link">
              {t('auth.sign_up.login')}
            </Link>
          </Typography>
        </span>
      </div>

      <div className="flex flex-col gap-4 px-1 pt-8">
        <ControlledInput
          disabled={true}
          control={control}
          label={t('auth.sign_up.label_company_name')}
          type="text"
          name="companyName"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_company_name')}
        />
        <ControlledInput
          disabled={true}
          control={control}
          label={t('auth.sign_up.label_email')}
          type="text"
          name="email"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_email')}
        />
        <div>
          <ControlledInput
            control={control}
            error={errors.password}
            label={t('auth.sign_up.label_password')}
            type="password"
            name="password"
            classNameParent="w-full"
            autoComplete="new-password"
            role="presentation"
            placeholder={t('auth.sign_up.placeholder_password')}
          />
          <PasswordValidator password={password} />
        </div>
        <ControlledInput
          control={control}
          error={errors.passwordConfirmation}
          label={t('auth.sign_up.label_confirm_password')}
          type="password"
          name="passwordConfirmation"
          autoComplete="new-password"
          role="presentation"
          classNameParent="w-full"
          placeholder={t('auth.sign_up.placeholder_confirm_password')}
        />
        <ControlledInput
          control={control}
          error={errors.termsAccepted}
          type="checkbox"
          name="termsAccepted"
          label={
            <Typography variant="regular5">
              {t('auth.sign_up.agree_terms')}
              <a
                href={TERMS_CONDITIONS_LINK}
                target="_blank"
                className="mx-1 cursor-pointer text-secondary-link"
              >
                {t('auth.sign_up.terms_conditions')}
              </a>
              {t('auth.sign_up.and')}
              <a
                href={PRIVACY_POLICY_LINK}
                target="_blank"
                className="ml-1 cursor-pointer text-secondary-link"
              >
                {t('auth.sign_up.privacy_policy')}
              </a>
              .
            </Typography>
          }
        />
        <div className="w-full">
          <Button
            onClick={onSubmit}
            onKeyDown={handlerEnter(onSubmit)}
            label={t('auth.accept_invitation.accept')}
            className="w-full"
          />
        </div>
      </div>
    </>
  );
}
